import React, { useEffect, useState } from 'react';
import { MapPinned } from 'lucide-react';
import { api, type Mark } from '../api';
import { useTheme } from '../theme';
import { Button, Icon, Textarea } from '../components/ui';
import { MarkGransFranFastighet } from './MarkGransFranFastighet';

/**
 * EK1 · Under Markinfo: markens fastighetsbeteckningar och verktyget som lägger markgränsen
 * ovanpå Lantmäteriets fastighetsgränser. Ändras av den som får ändra marken.
 */
export function MarkFastighet({ mark, canEdit, onMarkChanged }: { mark: Mark; canEdit: boolean; onMarkChanged: (patch: Partial<Mark>) => void }) {
  const T = useTheme();
  const [list, setList] = useState<string[] | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tool, setTool] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { api.markFastighetsbeteckningar(mark.id).then(setList).catch(() => setList([])); }, [mark.id]);

  const save = async () => {
    if (draft == null) return;
    setBusy(true); setErr('');
    try {
      const saved = await api.setMarkFastighet(mark.id, { fastighetsbeteckningar: draft.split('\n') });
      setList(saved.fastighetsbeteckningar);
      setDraft(null);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Kunde inte spara'); }
    setBusy(false);
  };

  const label: React.CSSProperties = { color: T.textMuted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 };
  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }} data-testid="mark-fastighet">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={label}>Fastighetsbeteckningar</div>
        {canEdit && draft == null && <Button size="sm" variant="ghost" onClick={() => setDraft((list ?? []).join('\n'))} data-testid="fastighet-andra">Ändra</Button>}
      </div>
      {draft == null ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-testid="fastighet-lista">
          {list == null ? <span style={{ color: T.textMuted, fontSize: 14 }}>Laddar…</span>
            : list.length ? list.map(b => <span key={b} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 14, fontWeight: 600, color: T.text }}>{b}</span>)
            : <span style={{ color: T.textMuted, fontSize: 14 }}>Inga fastighetsbeteckningar angivna.</span>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Textarea rows={4} value={draft} onChange={e => setDraft(e.target.value)} placeholder={'En per rad, t.ex.\nGnesta Frösslunda 1:23\nGnesta Ekeby 4:1'} aria-label="Fastighetsbeteckningar, en per rad" data-testid="fastighet-text" />
          <div style={{ fontSize: 12, color: T.textMuted }}>Beteckningen syns på kartan när lagret Fastighetsgränser är på (t.ex. "Frösslunda 1:23").</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" loading={busy} onClick={save} data-testid="fastighet-spara-beteckning">Spara</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setDraft(null); setErr(''); }}>Avbryt</Button>
          </div>
        </div>
      )}
      {err && <div role="alert" style={{ color: T.danger, fontSize: 13, fontWeight: 600, marginTop: 6 }}>{err}</div>}

      {canEdit && (
        <div style={{ marginTop: 14 }}>
          <Button variant="secondary" icon={<Icon as={MapPinned} size={16} />} onClick={() => { setTool(true); setMsg(''); }} data-testid="fastighet-oppna">
            Lägg markgräns efter fastighetskartan
          </Button>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>Tryck inuti fastigheten (en eller flera) så följer gränsen Lantmäteriets linjer. Du kan justera innan du sparar.</div>
          {msg && <div role="status" style={{ color: T.ok, fontSize: 13, fontWeight: 600, marginTop: 6 }}>{msg}</div>}
        </div>
      )}
      {tool && (
        <MarkGransFranFastighet mark={mark} onClose={() => setTool(false)}
          onSaved={m => { onMarkChanged({ coordinatesJson: m.coordinatesJson ?? undefined, areaHa: m.areaHa ?? undefined, mapLat: m.mapLat ?? undefined, mapLng: m.mapLng ?? undefined } as Partial<Mark>); setMsg('Markgränsen är sparad.'); }} />
      )}
    </div>
  );
}
